import React from "react";
import { motion } from "framer-motion";
import Logo from "../components/images/logo.png";
import { Link } from "react-router-dom";
import { FaLinkedinIn, FaGithub } from "react-icons/fa";
import { FaSquareUpwork } from "react-icons/fa6";
import { TbBrandFiverr } from "react-icons/tb";
import {
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLocationMarker,
} from "react-icons/hi";
import { ContactUs } from "../constant/data";
import ContactForm from "./ContactForm";
import { useAnchorNav } from "../utils/anchors";

const iconMap = {
  Upwork: FaSquareUpwork,
  Fiverr: TbBrandFiverr,
  LinkedIn: FaLinkedinIn,
  GitHub: FaGithub,
  Email: HiOutlineMail,
  Phone: HiOutlinePhone,
  Address: HiOutlineLocationMarker,
};

const accentByTitle = {
  Upwork: "from-aurora-teal/30 to-aurora-mint/20",
  Fiverr: "from-aurora-teal/30 to-aurora-amber/20",
  LinkedIn: "from-aurora-teal/30 to-aurora-magenta/20",
  GitHub: "from-aurora-magenta/30 to-aurora-amber/20",
  Email: "from-aurora-magenta/30 to-aurora-rose/20",
  Phone: "from-aurora-amber/30 to-aurora-magenta/20",
  Address: "from-aurora-mint/30 to-aurora-teal/20",
};

function ContactTile({ entry, idx }) {
  const Icon = iconMap[entry.title] || HiOutlineMail;
  const accent = accentByTitle[entry.title] || "from-aurora-teal/30 to-aurora-magenta/20";
  const Tag = entry.link ? "a" : "div";
  const linkProps = entry.link
    ? entry.link.startsWith("mailto:") || entry.link.startsWith("tel:")
      ? { href: entry.link }
      : { href: entry.link, target: "_blank", rel: "noreferrer" }
    : {};

  return (
    <motion.div
      initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.75,
        delay: idx * 0.06,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Tag
        {...linkProps}
        className="group relative block rounded-2xl border border-white/10 !p-5 md:!p-6 overflow-hidden transition-all hover:border-aurora-teal/30 hover:-translate-y-0.5"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
        }}
      >
        <div
          className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${accent} blur-2xl opacity-60 group-hover:opacity-100 transition-opacity`}
        />
        <div className="relative flex items-start !gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-aurora-teal group-hover:scale-105 transition-transform">
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
              {entry.title}
            </div>
            <div className="mt-1 text-[13px] md:text-sm text-white break-words leading-snug">
              {breakEmail(entry.details)}
            </div>
            {entry.link && (
              <div className="mt-1 inline-flex items-center text-[11px] text-aurora-teal/90 opacity-0 group-hover:opacity-100 transition-opacity">
                Open
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="ml-1"
                >
                  <path
                    d="M7 17 17 7M17 7H8M17 7v9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      </Tag>
    </motion.div>
  );
}

// Long addresses break before the "@" instead of mid-word.
const breakEmail = (text) => {
  if (typeof text !== "string" || !text.includes("@")) return text;
  const [local, domain] = text.split("@");
  return (
    <>
      {local}
      <wbr />
      {"@" + domain}
    </>
  );
};

const Section10 = () => {
  const goTo = useAnchorNav();
  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section
      id="contact"
      className="relative w-full px-6 md:px-12 lg:px-24 py-20 md:py-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        {/* Big CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-3xl overflow-hidden border border-white/10 p-8 md:p-16 lg:p-20"
          style={{
            background:
              "radial-gradient(800px 400px at 0% 0%, rgba(45,212,191,0.22), transparent 60%), radial-gradient(700px 400px at 100% 100%, rgba(236,72,153,0.20), transparent 60%), linear-gradient(180deg, rgba(10,14,32,0.7), rgba(4,6,13,0.9))",
          }}
        >
          <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-aurora-teal/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-aurora-magenta/30 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="eyebrow">
                <span className="dot" />
                Start a project
              </span>
              <h2 className="mt-6 font-jamjuree text-3xl md:text-5xl font-semibold text-white leading-[1.1]">
                Tell us what you
                <br />
                want to <span className="text-gradient-aurora">build</span>.
              </h2>
              <p className="mt-6 text-sm md:text-base text-white/65 max-w-xl leading-relaxed">
                Web, mobile or desktop, a new product or an existing one. Send a
                few lines and we reply within 24 hours with questions, a rough
                plan and an estimate.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="mailto:hmtechnologies.work@gmail.com"
                  className="btn-ghost"
                >
                  Email us directly
                </a>
                <a
                  href="https://wa.me/923158786614"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                >
                  WhatsApp
                </a>
              </div>
            </div>

            <ContactForm compact />
          </div>
        </motion.div>

        {/* Hire methods */}
        <div className="mt-12 md:mt-16">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <span className="eyebrow">
                <span className="dot" />
                Find us on
              </span>
              <h3 className="mt-3 font-jamjuree text-xl md:text-2xl font-semibold text-white">
                Pick the channel that suits you.
              </h3>
            </div>
            <p className="text-xs md:text-sm text-white/50 max-w-sm">
              Reaches us directly. We usually reply within 24 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {ContactUs.map((entry, idx) => (
              <ContactTile key={entry.id} entry={entry} idx={idx} />
            ))}
          </div>
        </div>

        {/* Footer nav row */}
        <div className="mt-12 flex flex-col lg:flex-row items-center justify-between gap-4 pt-8 border-t border-white/5">
          <div className="flex items-center gap-3" onClick={scrollTop}>
            <Link to="/">
              <img src={Logo} alt="Logo" className="h-8" />
            </Link>
            <span className="text-xs text-white/40">
              © {new Date().getFullYear()} Hamza Mehboob.
            </span>
          </div>

          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/65">
            <li>
              <a href="/#top" onClick={goTo("#top")} className="hover:text-white transition-colors">
                Home
              </a>
            </li>
            <li>
              <a href="/#about" onClick={goTo("#about")} className="hover:text-white transition-colors">
                About
              </a>
            </li>
            <li>
              <a href="/#portfolio" onClick={goTo("#portfolio")}
                className="hover:text-white transition-colors"
              >
                Portfolio
              </a>
            </li>
            <li>
              <a href="/#services" onClick={goTo("#services")}
                className="hover:text-white transition-colors"
              >
                Services
              </a>
            </li>
            <li>
              <a href="/#reviews" onClick={goTo("#reviews")}
                className="hover:text-white transition-colors"
              >
                Reviews
              </a>
            </li>
            <li>
              <Link to="/contact" className="hover:text-white transition-colors">
                Contact
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Section10;
